/**
 * Ask Lens trust strip routing badges — friendly labels from retrieval_debug.
 * Never exposes raw execution_trace enums or internal class names.
 */

export const TRUST_BADGE_LABELS = {
    LIVE_DATABASE: 'Live Database',
    PROJECT_CATALOG: 'Project Catalog',
    VERIFIED_EVIDENCE: 'Verified Evidence',
    AI_SYNTHESIS: 'AI Synthesis',
    MIXED_SOURCES: 'Mixed Sources',
    CONVERSATION_CONTEXT: 'Conversation Context',
    NEEDS_CLARIFICATION: 'Needs clarification',
};

const EXECUTION_PATH_ALIASES = {
    db_short_circuit: 'database',
    database: 'database',
    vdr_catalog: 'catalog',
    catalog: 'catalog',
    tool_planner: 'planner',
    tool_planner_mixed: 'planner',
    tool_planner_pure_rag: 'planner',
    tool_planner_no_plan: 'planner',
    tool_planner_terminated: 'planner',
    planner: 'planner',
    hybrid: 'hybrid',
    grounded_rag: 'grounded_rag',
    legacy_rag: 'legacy_rag',
    clarify: 'clarify',
};

const ROUTING_BADGE_BY_KIND = {
    database: { key: 'database', label: TRUST_BADGE_LABELS.LIVE_DATABASE, tone: 'success' },
    catalog: { key: 'catalog', label: TRUST_BADGE_LABELS.PROJECT_CATALOG, tone: 'success' },
    planner: { key: 'planner', label: TRUST_BADGE_LABELS.MIXED_SOURCES, tone: 'success' },
    hybrid: { key: 'hybrid', label: TRUST_BADGE_LABELS.MIXED_SOURCES, tone: 'success' },
    grounded_rag: {
        key: 'verified_evidence',
        label: TRUST_BADGE_LABELS.VERIFIED_EVIDENCE,
        tone: 'success',
    },
    legacy_rag: { key: 'ai_synthesis', label: TRUST_BADGE_LABELS.AI_SYNTHESIS, tone: 'neutral' },
    clarify: {
        key: 'clarify',
        label: TRUST_BADGE_LABELS.NEEDS_CLARIFICATION,
        tone: 'warning',
    },
};

/** Map legacy answer_provenance strings to friendly routing labels. */
const PROVENANCE_FRIENDLY = {
    'live irl records': TRUST_BADGE_LABELS.LIVE_DATABASE,
    'live task records': TRUST_BADGE_LABELS.LIVE_DATABASE,
    'live vdr records': TRUST_BADGE_LABELS.LIVE_DATABASE,
    'live raid records': TRUST_BADGE_LABELS.LIVE_DATABASE,
    'live lens records': TRUST_BADGE_LABELS.LIVE_DATABASE,
    'canonical lens analysis': TRUST_BADGE_LABELS.LIVE_DATABASE,
};

function normalizeRetrievalDebug(retrievalDebug) {
    return retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
}

export function isGroundedSynthesisResponse(retrievalDebug) {
    const rd = normalizeRetrievalDebug(retrievalDebug);
    return Boolean(
        rd.grounded_synthesis_applied || rd.grounded_synthesis_enabled || rd.grounded_synthesis,
    );
}

export function isVdrCatalogResponse(retrievalDebug) {
    const rd = normalizeRetrievalDebug(retrievalDebug);
    const intent = String(rd.router_intent || rd.intent || '').toLowerCase();
    if (intent === 'vdr_catalog') return true;
    return Boolean(rd.vdr_catalog && typeof rd.vdr_catalog === 'object');
}

export function isDbToolResponse(retrievalDebug) {
    const rd = normalizeRetrievalDebug(retrievalDebug);
    const mode = String(rd.router_mode || '').toLowerCase();
    const intent = String(rd.router_intent || rd.intent || '').toLowerCase();
    const tracePath = String(rd.execution_trace?.path || '').toLowerCase();
    if (intent === 'db_tools') return true;
    if (['db', 'canonical', 'clarify'].includes(mode)) return true;
    if (tracePath === 'db_short_circuit' || tracePath === 'database') return true;
    return (
        Array.isArray(rd.answer_provenance) &&
        rd.answer_provenance.length > 0 &&
        !rd.db_tool_hybrid_merge
    );
}

export function normalizeExecutionPathKind(retrievalDebug) {
    const rd = normalizeRetrievalDebug(retrievalDebug);
    const tracePath = String(rd.execution_trace?.path || '')
        .toLowerCase()
        .trim();
    const routerMode = String(rd.router_mode || '').toLowerCase().trim();
    const routerIntent = String(rd.router_intent || rd.intent || '').toLowerCase().trim();

    if (tracePath) {
        const alias = EXECUTION_PATH_ALIASES[tracePath];
        if (alias === 'legacy_rag') {
            return isGroundedSynthesisResponse(rd) ? 'grounded_rag' : 'legacy_rag';
        }
        if (alias) return alias;
    }

    if (routerMode === 'clarify') return 'clarify';
    if (isVdrCatalogResponse(rd)) return 'catalog';
    if (isDbToolResponse(rd) && !rd.db_tool_hybrid_merge) return 'database';
    if (routerMode === 'tool_planner' || routerIntent === 'tool_planner') return 'planner';
    if (rd.db_tool_hybrid_merge || routerMode === 'hybrid') return 'hybrid';

    if (isGroundedSynthesisResponse(rd)) return 'grounded_rag';
    if (routerMode === 'rag' || tracePath === 'legacy_rag') return 'legacy_rag';

    return null;
}

export function mapAnswerProvenanceToFriendlyLabel(provenanceEntry) {
    const raw = String(provenanceEntry || '').trim();
    if (!raw) return null;
    const mapped = PROVENANCE_FRIENDLY[raw.toLowerCase()];
    if (mapped) return mapped;
    if (/^live .+ records$/i.test(raw)) return TRUST_BADGE_LABELS.LIVE_DATABASE;
    return null;
}

function resolveProvenanceRoutingLabel(retrievalDebug) {
    const rd = normalizeRetrievalDebug(retrievalDebug);
    const provenance = Array.isArray(rd.answer_provenance)
        ? rd.answer_provenance.filter(Boolean)
        : [];
    for (const entry of provenance) {
        const friendly = mapAnswerProvenanceToFriendlyLabel(entry);
        if (friendly) return friendly;
    }
    return null;
}

function addBadge(badges, seen, badge) {
    if (!badge?.label) return;
    const token = String(badge.label).toLowerCase();
    if (seen.has(token)) return;
    seen.add(token);
    badges.push(badge);
}

/**
 * Resolve ordered, deduplicated trust strip routing badges for an Ask response.
 */
export function resolveTrustStripRoutingBadges(retrievalDebug) {
    const rd = normalizeRetrievalDebug(retrievalDebug);
    const badges = [];
    const seen = new Set();

    const pathKind = normalizeExecutionPathKind(rd);
    if (pathKind && ROUTING_BADGE_BY_KIND[pathKind]) {
        addBadge(badges, seen, { ...ROUTING_BADGE_BY_KIND[pathKind] });
    } else {
        const provenanceLabel = resolveProvenanceRoutingLabel(rd);
        if (provenanceLabel) {
            addBadge(badges, seen, {
                key: 'provenance',
                label: provenanceLabel,
                tone: 'success',
            });
        }
    }

    const showVerifiedEvidence =
        isGroundedSynthesisResponse(rd) &&
        pathKind !== 'grounded_rag' &&
        pathKind !== 'legacy_rag';

    if (showVerifiedEvidence) {
        addBadge(badges, seen, { ...ROUTING_BADGE_BY_KIND.grounded_rag });
    }

    const historyTurns = Number(rd.conversation_history_turns);
    if (Number.isFinite(historyTurns) && historyTurns > 0) {
        addBadge(badges, seen, {
            key: 'conversation_context',
            label: TRUST_BADGE_LABELS.CONVERSATION_CONTEXT,
            tone: 'neutral',
        });
    }

    if (badges.length === 0) {
        if (pathKind === null && (rd.fused_count > 0 || rd.post_fusion_total > 0)) {
            addBadge(badges, seen, {
                ...(isGroundedSynthesisResponse(rd)
                    ? ROUTING_BADGE_BY_KIND.grounded_rag
                    : ROUTING_BADGE_BY_KIND.legacy_rag),
            });
        }
    }

    return badges;
}

export function resolveTrustStripEvidenceBand(retrievalDebug, sourceCount) {
    const rd = normalizeRetrievalDebug(retrievalDebug);
    const routingBadges = resolveTrustStripRoutingBadges(rd);
    const routingLabels = new Set(routingBadges.map(b => String(b.label).toLowerCase()));

    if (routingLabels.has(TRUST_BADGE_LABELS.LIVE_DATABASE.toLowerCase())) {
        return null;
    }
    if (routingLabels.has(TRUST_BADGE_LABELS.PROJECT_CATALOG.toLowerCase())) {
        return null;
    }
    if (routingLabels.has(TRUST_BADGE_LABELS.VERIFIED_EVIDENCE.toLowerCase())) {
        return null;
    }
    if (routingLabels.has(TRUST_BADGE_LABELS.AI_SYNTHESIS.toLowerCase())) {
        return null;
    }
    if (String(rd.router_mode || '').toLowerCase() === 'clarify') {
        return { label: TRUST_BADGE_LABELS.NEEDS_CLARIFICATION, tone: 'warning' };
    }

    const provenance = Array.isArray(rd.answer_provenance)
        ? rd.answer_provenance.filter(Boolean)
        : [];
    if (provenance.length) {
        const friendly = provenance
            .map(mapAnswerProvenanceToFriendlyLabel)
            .filter(Boolean);
        if (friendly.length) {
            return { label: friendly[0], tone: 'success' };
        }
    }

    if (!sourceCount) {
        return { label: 'No supporting evidence', tone: 'danger' };
    }
    return { label: 'Partial evidence', tone: 'warning' };
}
